/**
 * The product window's behavior half (studio-shop D4): the product a grid
 * opened on this page, its options grouped by format, the picked option (the
 * first on sale until the buyer picks), and Add to bag, which keeps the price
 * the buyer saw so a later change is marked in the bag, then closes the window
 * and opens the page's bag. Escape closes it and the arrow keys step through
 * the grid, whichever look draws it. Placed with a handle (`product`), it is
 * that product where it is placed: always open, not the page's window, and Add
 * to bag opens the page's bag; a handle the shop does not show warns once.
 * Draws nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { shippingLine, shopPrice, useShopCatalog } from '../../api/shop';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { BAG_MAX_LINES, BAG_MAX_QUANTITY, useBag } from '../../hooks/useBag';
import { usePlaceOnShopPage, useShopPage } from '../../hooks/useShopPage';
import { contract, type ShopProductActions, type ShopProductEvents, type ShopProductFormat, type ShopProductState } from './contract';

export type ShopProductRuntime = PieceRuntime<typeof contract, ShopProductState, ShopProductActions>;

/** How long "Added to your bag" shows before the window gives way to the bag. */
const ADDED_BEAT_MS = 450;

const formatLabel = (format: string | null) => (format ? format.charAt(0).toUpperCase() + format.slice(1) : 'Standard');

export function useShopProduct(input: PieceInput<ShopProductEvents> = {}): ShopProductRuntime {
  const options = usePieceOptions(contract, input.options);
  const { page, state: shopPage } = useShopPage();
  // shop-catalog: a product placed by its handle is drawn where it is placed; without one, this is the page's window.
  const placedHandle = options.product;
  usePlaceOnShopPage(page, 'windows', placedHandle === null);
  const open = placedHandle === null ? shopPage.product : null;
  const handle = placedHandle ?? open?.handle ?? null;
  const catalog = useShopCatalog();
  const bag = useBag();
  const product = (handle && catalog.data?.products.find((p) => p.handle === handle)) || null;
  const currency = catalog.data?.currency ?? 'USD';
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [adding, setAdding] = useState<ShopProductState['adding']>('idle');

  useEffect(() => {
    setPickedId(null);
    setAdding('idle');
  }, [handle]);

  useEffect(() => {
    if (adding !== 'added') return;
    const timer = setTimeout(() => {
      if (page.hasDrawer()) page.openBag();
      // A placed product stays where it is, ready for another.
      if (placedHandle !== null) setAdding('idle');
      else if (!page.hasDrawer()) page.closeProduct();
    }, ADDED_BEAT_MS);
    return () => clearTimeout(timer);
  }, [adding, page, placedHandle]);

  // An unknown or hidden handle draws nothing, and says so once, naming it.
  const warned = useRef(false);
  const unknown = placedHandle !== null && Boolean(catalog.data) && !product;
  useEffect(() => {
    if (!unknown || warned.current) return;
    warned.current = true;
    console.warn(`[telify] shop-product: no product "${placedHandle}" on this shop`);
  }, [unknown, placedHandle]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') page.closeProduct();
      if (e.key === 'ArrowLeft') page.step(-1);
      if (e.key === 'ArrowRight') page.step(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, page]);

  const all = useMemo(() => product?.options ?? [], [product]);
  const picked = all.find((o) => o.id === pickedId) ?? all[0] ?? null;

  // The small copy of the picture an option is drawn with (its own, else the product's first), so a drawing can fetch
  // every choice's picture before it is picked.
  const smallOf = useCallback((o: (typeof all)[number]) => (o.image ?? product?.images[0])?.small ?? null, [product]);
  const formats = useMemo<ShopProductFormat[]>(() => {
    const seen = new Map<string | null, (typeof all)[number]>();
    for (const o of all) if (!seen.has(o.format)) seen.set(o.format, o);
    return [...seen].map(([format, o]) => ({ format, label: formatLabel(format), optionId: o.id, selected: picked?.format === format, small: smallOf(o) }));
  }, [all, picked, smallOf]);

  const { add, lines } = bag;
  const actions = useMemo<ShopProductActions>(
    () => ({
      pickOption: (optionId) => {
        setPickedId(optionId);
        setAdding('idle');
      },
      addToBag: () => {
        // shop-self-fulfilment D5: none left of it, so its button does nothing, whichever look draws it.
        if (!picked || picked.soldOut) return;
        setAdding(add(picked.id, picked.price) ? 'added' : 'full');
      },
      // A product placed by its handle has no window to close or step through.
      close: () => placedHandle === null && page.closeProduct(),
      previous: () => placedHandle === null && page.step(-1),
      next: () => placedHandle === null && page.step(1),
    }),
    [add, picked, page, placedHandle],
  );

  const inBag = picked ? (lines.find((l) => l.optionId === picked.id)?.quantity ?? 0) : 0;
  // D3: the picked size's own picture in that size's shape, else the product's first, drawn whole.
  const image = picked?.image ?? product?.images[0];
  const shapedBy = picked?.image ? picked.size : null;
  const prices = all.map((o) => Number(o.price));
  const money = (amount: number) => shopPrice(amount.toFixed(2), currency);
  const at = open ? open.handles.indexOf(open.handle) : -1;
  return {
    state: {
      open: handle !== null,
      status: !handle ? 'closed' : product ? 'ready' : catalog.isPending ? 'loading' : 'missing',
      handle,
      title: product?.title ?? '',
      description: product?.description ?? null,
      image: image ? { url: image.url, small: image.small ?? null, alt: image.alt ?? product?.title ?? '', size: shapedBy } : null,
      formats,
      sizes: all
        .filter((o) => o.format === picked?.format)
        .map((o) => ({ optionId: o.id, label: o.size ?? o.label, price: shopPrice(o.price, currency), selected: o.id === picked?.id, small: smallOf(o) })),
      picked: picked ? { optionId: picked.id, label: picked.label, price: shopPrice(picked.price, currency) } : null,
      soldOut: picked?.soldOut === true,
      from: prices.length ? money(Math.min(...prices)) : null,
      to: prices.length ? money(Math.max(...prices)) : null,
      shipping: shippingLine(catalog.data?.freeShippingCountries ?? []),
      adding,
      full:
        adding !== 'full'
          ? null
          : inBag >= BAG_MAX_QUANTITY
            ? `Your bag already holds ${BAG_MAX_QUANTITY} of these, the most it takes.`
            : `Your bag holds ${BAG_MAX_LINES} different items at most. Remove one to add this.`,
      position: open && open.handles.length > 1 && at >= 0 ? `${at + 1} of ${open.handles.length}` : null,
    },
    actions,
    options,
  };
}
